import type { CSSProperties } from 'react';
import { Link, Outlet, createFileRoute, useLocation } from '@tanstack/react-router';
import { Card } from '@osd/ui/card';
import { Heading } from '@osd/ui/heading';
import { Stack } from '@osd/ui/stack';
import { Text } from '@osd/ui/text';

/**
 * /components — pathless layout for the design-system docs surface.
 *
 * Every route under `/components/<name>` renders inside this layout,
 * which provides a side-nav with links to every per-component page.
 * New primitives that need live docs (added under root/osd#28) ship
 * with a new sibling file at `ui._layout.<name>.tsx` and one
 * entry in `COMPONENT_NAV` below — single source of truth.
 *
 * URL routes:
 *   /components             → overview index
 *   /components/<name>      → per-component page
 *
 *   The Vite router plugin reads `ui._layout.*.tsx` and
 *   `ui._layout.index.tsx` to regenerate `routeTree.gen.ts`.
 */

/**
 * Component categories for the /ui overview page.
 *
 * `category` groups adjacent components in the overview index so
 * the reader has anchor points instead of a flat 37-card list.
 * Used by the `/ui` index only — the per-component side nav
 * stays alphabetical.
 *
 * Categories (in display order):
 *   - Actions:    clickable triggers (button, button-group, modal)
 *   - Inputs:     form controls (checkbox, input, select, …)
 *   - Feedback:   status + help (alert, badge, progress, tooltip)
 *   - Layout:     page chrome + structure (grid, stack, card)
 *   - Forms:      form-state helpers (form, use-form, fieldset, …)
 *   - Navigation: nav surfaces (sidebar, tabs, pagination)
 *   - Foundations: design tokens + utility classes (utilities, theme-switch)
 */
export const COMPONENT_CATEGORIES = [
  'Actions',
  'Inputs',
  'Feedback',
  'Layout',
  'Forms',
  'Navigation',
  'Foundations',
] as const;
export type ComponentCategory = (typeof COMPONENT_CATEGORIES)[number];

/**
 * One typed entry per live docs page. `slug` is the URL segment
 * (matches the route file name under `ui._layout.<slug>.tsx`);
 * `to` is the route's full path. Both are required and must stay
 * in sync — the `to` value is widened with `as const` so the TanStack
 * `Link` type-checks against the registered route union.
 *
 * To add a new component docs page:
 *   1. Append a new entry to this list.
 *   2. Create the matching route file
 *      (`ui._layout.<slug>.tsx`) that renders the page.
 * Both steps are required.
 *
 * Keep alphabetical within each category for predictability.
 */
export const COMPONENT_NAV = [
  { 
    category: 'Feedback',
    slug: 'alert',
    label: 'Alert',
    summary: 'Inline status message (info/success/warning/danger).',
    to: '/ui/alert' as const,
  },
  { 
    category: 'Feedback',
    slug: 'badge',
    label: 'Badge',
    summary: 'Small inline numerical indicator (inbox counts, notification counters).',
    to: '/ui/badge' as const,
  },
  { 
    category: 'Feedback',
    slug: 'banner',
    label: 'Banner',
    summary: 'Full-width contextual notice with title, body, optional action, optional dismiss.',
    to: '/ui/banner' as const,
  },
  { 
    category: 'Layout',
    slug: 'empty-state',
    label: 'EmptyState',
    summary: 'Centered zero-data placeholder (icon + title + description + actions).',
    to: '/ui/empty-state' as const,
  },
  { 
    category: 'Actions',
    slug: 'button',
    label: 'Button',
    summary: 'Action trigger with default/primary/danger/ghost variants.',
    to: '/ui/button' as const,
  },
  { 
    category: 'Actions',
    slug: 'button-group',
    label: 'ButtonGroup',
    summary: 'Fused bordered toolbar (horizontal default; vertical modifier).',
    to: '/ui/button-group' as const,
  },
  { 
    category: 'Layout',
    slug: 'card',
    label: 'Card',
    summary: 'Surface container with title/body compound parts.',
    to: '/ui/card' as const,
  },
  { 
    category: 'Layout',
    slug: 'code-block',
    label: 'CodeBlock',
    summary: 'Docs-site helper: Shiki-highlighted snippet + copy-to-clipboard button.',
    to: '/ui/code-block' as const,
  },
  { 
    category: 'Inputs',
    slug: 'checkbox',
    label: 'Checkbox',
    summary: 'Standalone boolean input; label sits next to the control.',
    to: '/ui/checkbox' as const,
  },
  { 
    category: 'Inputs',
    slug: 'color-picker',
    label: 'ColorPicker',
    summary: 'Native color input + typed hex entry. Zero dependencies.',
    to: '/ui/color-picker' as const,
  },
  { 
    category: 'Feedback',
    slug: 'error-summary',
    label: 'ErrorSummary',
    summary: 'GOV.UK-style top-of-form error list. Nothing when empty.',
    to: '/ui/error-summary' as const,
  },
  { 
    category: 'Inputs',
    slug: 'file-input',
    label: 'FileInput',
    summary: 'Native file picker + visible filename + Clear button.',
    to: '/ui/file-input' as const,
  },
  { 
    category: 'Forms',
    slug: 'fieldset',
    label: 'Fieldset',
    summary: 'Semantic <fieldset> + <legend> for grouped form controls.',
    to: '/ui/fieldset' as const,
  },
  { 
    category: 'Forms',
    slug: 'form',
    label: 'Form',
    summary: 'Primitives + composites (LoginForm) + handling (useForm). One stop for form docs.',
    to: '/ui/form' as const,
  },
  { 
    category: 'Layout',
    slug: 'grid',
    label: 'Grid + Row + Col',
    summary: 'Flexbox 12-column system with responsive spans + offsets.',
    to: '/ui/grid' as const,
  },
  { 
    category: 'Layout',
    slug: 'heading',
    label: 'Heading',
    summary: 'Semantic H1–H6 with level-controlled typography.',
    to: '/ui/heading' as const,
  },
  { 
    category: 'Inputs',
    slug: 'input',
    label: 'Field + Input',
    summary: 'Labeled form field with auto id-binding and input types.',
    to: '/ui/input' as const,
  },
  { 
    category: 'Inputs',
    slug: 'input-group',
    label: 'InputGroup',
    summary: 'Borderless prefix/suffix addon fused with a field control.',
    to: '/ui/input-group' as const,
  },
  { 
    category: 'Forms',
    slug: 'label',
    label: 'Label',
    summary: 'Quiet monochrome inline tag (category / metadata prefix).',
    to: '/ui/label' as const,
  },
  { 
    category: 'Actions',
    slug: 'modal',
    label: 'Modal',
    summary: 'Portal-based dialog with focus trap + scroll lock + Escape + backdrop.',
    to: '/ui/modal' as const,
  },
  { 
    category: 'Forms',
    slug: 'loading-state',
    label: 'LoadingState',
    summary: 'Composite that maps the query tri-state (loading/error/ready) onto one render call.',
    to: '/ui/loading-state' as const,
  },
  { 
    category: 'Feedback',
    slug: 'progress',
    label: 'Progress',
    summary: 'Single value or stacked bars (success / warning / danger variants).',
    to: '/ui/progress' as const,
  },
  { 
    category: 'Inputs',
    slug: 'range',
    label: 'Range',
    summary: 'Continuous numeric slider with optional label + live value.',
    to: '/ui/range' as const,
  },
  { 
    category: 'Feedback',
    slug: 'skeleton',
    label: 'Skeleton',
    summary: 'Content-shape placeholder (rect / text / circle) for loading states.',
    to: '/ui/skeleton' as const,
  },
  { 
    category: 'Inputs',
    slug: 'radio',
    label: 'Radio',
    summary: 'Standalone radio input; group via shared name.',
    to: '/ui/radio' as const,
  },
  { 
    category: 'Inputs',
    slug: 'select',
    label: 'Select',
    summary: 'Native <select> with custom caret (no JS dep).',
    to: '/ui/select' as const,
  },
  { 
    category: 'Navigation',
    slug: 'sidebar',
    label: 'Sidebar',
    summary: 'Primary nav surface (Sidebar + Section + Link, router-agnostic).',
    to: '/ui/sidebar' as const,
  },
  { 
    category: 'Layout',
    slug: 'stack',
    label: 'Stack',
    summary: 'Flexbox layout primitive (row/column + gap tokens).',
    to: '/ui/stack' as const,
  },
  { 
    category: 'Feedback',
    slug: 'spinner',
    label: 'Spinner',
    summary: 'Animated loading glyph (5 sizes, role=status + aria-live=polite).',
    to: '/ui/spinner' as const,
  },
  { 
    category: 'Feedback',
    slug: 'tooltip',
    label: 'Tooltip',
    summary: 'Positioned contextual help on hover/focus (compound parts).',
    to: '/ui/tooltip' as const,
  },
  { 
    category: 'Inputs',
    slug: 'switch',
    label: 'Switch',
    summary: 'Toggle-style boolean; role="switch" for assistive tech.',
    to: '/ui/switch' as const,
  },
  { 
    category: 'Layout',
    slug: 'table',
    label: 'Table',
    summary: 'Compound data-table primitive (Header/Body/Row/Head/Cell + SortableHead).',
    to: '/ui/table' as const,
  },
  { 
    category: 'Layout',
    slug: 'tabs',
    label: 'Tabs',
    summary: 'Compound tabs primitive (TabsList / TabsTab / TabsPanel). WAI-ARIA pattern.',
    to: '/ui/tabs' as const,
  },
  { 
    category: 'Layout',
    slug: 'text',
    label: 'Text',
    summary: 'Body copy with a muted modifier and inline mode.',
    to: '/ui/text' as const,
  },
  { 
    category: 'Inputs',
    slug: 'textarea',
    label: 'Textarea',
    summary: 'Multi-line text input; auto-binds to Field id.',
    to: '/ui/textarea' as const,
  },
  { 
    category: 'Foundations',
    slug: 'theme-switch',
    label: 'Theme switch',
    summary: 'User-controlled light/dark palette toggle (sun/moon icons, localStorage).',
    to: '/ui/theme-switch' as const,
  },
  { 
    category: 'Forms',
    slug: 'use-form',
    label: 'useForm',
    summary: 'Form-state hook: values / errors / touched / handleSubmit.',
    to: '/ui/use-form' as const,
  },
  { 
    category: 'Foundations',
    slug: 'utilities',
    label: 'Utility classes',
    summary: 'Spacing / text / display / flex / borders / shadows. ~1800 generated classes.',
    to: '/ui/utilities' as const,
  },
] as const satisfies ReadonlyArray<{
  readonly category: ComponentCategory;
  readonly slug: string;
  readonly label: string;
  readonly summary: string;
  readonly to:
    | '/ui/alert'
    | '/ui/badge'
    | '/ui/banner'
    | '/ui/button'
    | '/ui/empty-state'
    | '/ui/button-group'
    | '/ui/card'
    | '/ui/code-block'
    | '/ui/checkbox'
    | '/ui/color-picker'
    | '/ui/error-summary'
    | '/ui/file-input'
    | '/ui/fieldset'
    | '/ui/form'
    | '/ui/grid'
    | '/ui/heading'
    | '/ui/input'
    | '/ui/label'
    | '/ui/loading-state'
    | '/ui/input-group'
    | '/ui/modal'
    | '/ui/pill'
    | '/ui/progress'
    | '/ui/range'
    | '/ui/radio'
    | '/ui/select'
    | '/ui/sidebar'
    | '/ui/skeleton'
    | '/ui/spinner'
    | '/ui/stack'
    | '/ui/switch'
    | '/ui/table'
    | '/ui/tabs'
    | '/ui/theme-switch'
    | '/ui/tooltip'
    | '/ui/text'
    | '/ui/textarea'
    | '/ui/use-form'
    | '/ui/utilities';
}>;

export type ComponentNavItem = (typeof COMPONENT_NAV)[number];

export const Route = createFileRoute('/ui/_layout')({
  component: ComponentsLayout,
});

// Two-column shell: fixed-width side nav + flexible content. Uses
// inline styles because no shared SCSS module exists for this layout
// yet; the side-nav layout is the only chrome added by /components.
// If a second chrome layout gets added in the future (root/osd#29),
// promote these styles to `apps/web/src/components/<shell>.module.scss`.
const LAYOUT_STYLE: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '220px minmax(0, 1fr)',
  gap: '1.5rem',
  alignItems: 'start',
};
// The nav is sticky to the viewport top so the active section
// stays visible while scrolling long content (audit log, full
// table examples). Internal `max-height` + `overflow-y: auto` is
// what makes the full 35-entry list reachable when the viewport
// is short — without it, the nav naturally grows taller than the
// viewport and entries near the bottom (Table, Switch, useForm,
// Utility classes, …) sit below the fold where readers assume
// they don't exist (root/osd#105).
const NAV_CARD_STYLE: CSSProperties = {
  position: 'sticky',
  top: '1rem',
  maxHeight: 'calc(100vh - 2rem)',
  overflowY: 'auto',
};
const NAV_LIST_STYLE: CSSProperties = {
  listStyle: 'none',
  margin: 0,
  padding: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: '0.25rem',
};
const NAV_LINK_STYLE: CSSProperties = {
  display: 'block',
  padding: '0.375rem 0.5rem',
  borderRadius: 'var(--osd-radius-sm, 4px)',
  color: 'inherit',
  textDecoration: 'none',
};

function ComponentsLayout() {
  const location = useLocation();
  // The layout's H1 + intro only make sense on the `/components`
  // overview. Each per-component page (`/components/<slug>`) is
  // its own document — rendering "Components" as h1 there would
  // be wrong (the page's own h1 is the component name) AND break
  // the heading hierarchy. Hide the shared header on child routes.
  const onIndex = location.pathname === '/ui' || location.pathname === '/ui/';
  return (
    <Stack gap={5}>
      {onIndex && (
        <header>
          <Heading level={1}>Components</Heading>
          <Text muted>
            Every primitive exported from <code>@osd/ui</code>. One page per component; pick from
            the list below or jump back to the <Link to="/ui">overview</Link>.
          </Text>
        </header>
      )}
      <div style={LAYOUT_STYLE}>
        <nav aria-label="Component docs">
          <Card style={NAV_CARD_STYLE}>
            <Stack gap={2}>
              <Heading level={2}>Components</Heading>
              <ul style={NAV_LIST_STYLE}>
                {COMPONENT_NAV.map((item) => (
                  <li key={item.slug}>
                    <Link
                      to={item.to}
                      style={NAV_LINK_STYLE}
                      activeProps={{
                        style: {
                          ...NAV_LINK_STYLE,
                          backgroundColor: 'var(--osd-color-surface-2)',
                          // Explicit accent so the active link stays
                          // legible in both light + dark — the inherited
                          // link colour was unreadable on the surface-2
                          // background in dark mode (root/osd#53).
                          color: 'var(--osd-color-accent)',
                          fontWeight: 600,
                        },
                      }}
                    >
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </Stack>
          </Card>
        </nav>
        <section>
          <Outlet />
        </section>
      </div>
    </Stack>
  );
}
